import React, { useEffect, useMemo } from "react"

import _ from "lodash"

import { updateAirportSeasonsSelection } from "../containers/selectionSlice"

import { IMainFlightSelectorProps } from "./FlightSelector"
import { getTimeAndCodeFilteredAirportSeasons } from "./Utils"
import { ValueLabel } from "./generic/fields"

import DropdownField from "./generic/form/DropdownField"
import RadioBooleanField from "./generic/form/RadioBooleanField"
import { FieldName, FlightText } from "../common/text"
import { useAppDispatch, useAppSelector } from "../app/hooks"

export enum CodeType {
  ICAO = "ICAO",
  IATA = "IATA"
}

interface Props extends IMainFlightSelectorProps {
  codeType?: string
}

const { airport } = FlightText
const AirportSelector: React.FunctionComponent<Props> = ({
  airportSeasons,
  codeType,
  handleChange,
  handleAvailChange,
  hasError,
  errorMsg,
  touched,
  airportseasonSelectorConfig
}) => {
  const dispatch = useAppDispatch()
  const { season, countries, airports, useLocalTime, useICAOCode } =
    useAppSelector((state) => state["selectionReducer"].airportSeasonsSelector)

  const countriesData = useAppSelector((state) => state["appReducer"].countries)
  const useICAOCodeType: boolean = airportseasonSelectorConfig.selectCodeType
    ? useICAOCode
    : codeType === CodeType.ICAO

  const handleGetCountryName = (countryCode: string) => {
    const countryData = countriesData.find((cd) => cd.code === countryCode)
    return countryData ? countryData.name : "" //countryData.toString()
  }

  const handleGetCountryOpts = () => {
    const timeAirportSeasons = getTimeAndCodeFilteredAirportSeasons(
      useLocalTime,
      useICAOCodeType,
      airportSeasons
    )
    const countriesOpts = _.uniqBy(
      timeAirportSeasons.filter((as) => as.season === season),
      (a) => a.country
    )
    const opts: Array<ValueLabel> = _.sortBy(
      countriesOpts.map((as) => {
        return {
          label: handleGetCountryName(as.country),
          value: as.country.toString()
        }
      }),
      (o) => o.label
    )
    return opts
  }
  // const [airportsLength, setAirportsLength] = useState(0)

  //Select Countries
  const handleSelectCountries = (name: string, val: any) => {
    const _val = airportseasonSelectorConfig.dropdownMultiSelect
      ? val
      : val
      ? [val]
      : []
    const updSelectedAirports =
      _val.length > 0
        ? airports && airports.length
          ? airports.filter((sa) =>
              airportSeasons.find(
                (as) =>
                  as.airport === sa &&
                  (airportseasonSelectorConfig.dropdownMultiSelect
                    ? val.find((v: string) => v === as.country)
                    : val === as.country)
              )
            )
          : []
        : airports

    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.countries,
        value: _val
      })
    )
    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.airports,
        value: updSelectedAirports
      })
    )
    const error = val && val.length > 0 ? false : true

    if (
      airportseasonSelectorConfig.dropdownMultiSelect &&
      airports.length === 0
    ) {
      handleChange && handleChange("airports", val, error)
    }
  }
  //Select airports
  const handleSelectAirports = (name: string, val: Array<string>) => {
    const value = airportseasonSelectorConfig.dropdownMultiSelect
      ? val
      : val
      ? [val]
      : []
    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.airports,
        value
      })
    )

    handleChange && handleChange(name, val, value.length === 0)
    handleAvailChange && handleAvailChange(name, val, value.length === 0)
  }

  const handleChangeCodeType = (name: string, val: boolean) => {
    const timeCodeAirportSeasons = getTimeAndCodeFilteredAirportSeasons(
      useLocalTime,
      val,
      airportSeasons
    )
    const updAirports = airports.filter((a) =>
      timeCodeAirportSeasons.find((tas) => tas.airport === a)
    )
    const updCountries = countries.filter((c) =>
      timeCodeAirportSeasons.find((tas) => tas.country === c)
    )

    if (!_.isEqual(airports, updAirports)) {
      dispatch(
        updateAirportSeasonsSelection({
          field: FieldName.airports,
          value: updAirports
        })
      )
    }

    if (!_.isEqual(countries, updCountries)) {
      dispatch(
        updateAirportSeasonsSelection({
          field: FieldName.countries,
          value: updCountries
        })
      )
    }

    dispatch(
      updateAirportSeasonsSelection({
        field: FieldName.useICAOCode,
        value: val
      })
    )
  }

  useEffect(() => {
    if (useICAOCode !== useICAOCodeType) {
      dispatch(
        updateAirportSeasonsSelection({
          field: FieldName.useICAOCode,
          value: useICAOCodeType
        })
      )
    }
    // eslint-disable-next-line
  }, [useICAOCodeType])

  const countryOpts = season ? handleGetCountryOpts() : []
  const airportOpts = useMemo(() => {
    const timeAirportSeasons = getTimeAndCodeFilteredAirportSeasons(
      useLocalTime,
      useICAOCodeType,
      airportSeasons
    )
    const airportOpts = timeAirportSeasons.filter(
      (as) =>
        as.season === season &&
        (countries.length ? countries.indexOf(as.country) >= 0 : true)
    )
    const opts: Array<ValueLabel> = _.sortBy(
      airportOpts.map((as) => ({
        label: useICAOCodeType
          ? as.airportIcao.toString()
          : as.airport.toString(),
        value: as.airport.toString()
      })),
      (o) => !useICAOCodeType ? o.value : o.label
    )
    return opts
  }, [airportSeasons, season, useLocalTime, useICAOCodeType, countries])

  useEffect(() => {
    if (
      !airportseasonSelectorConfig.dropdownMultiSelect &&
      airports.length !== 1 &&
      airportOpts.length
    ) {
      dispatch(
        updateAirportSeasonsSelection({
          field: FieldName.airports,
          value: airports.length === 0 ? [airportOpts[0].value] : [airports[0]]
        })
      )
    }

    // eslint-disable-next-line
  }, [
    airportOpts,
    airports,
    dispatch,
    airportseasonSelectorConfig.dropdownMultiSelect
  ])

  let predefinedAirport = airports && airports.length > 0 ? airports[0] : ""

  return (
    <div className="d-flex">
      <div className="ocs-mr-12 ocs-w-220">
        <DropdownField
          name={FieldName.countries}
          caption={
            airportseasonSelectorConfig.dropdownMultiSelect
              ? "Countries"
              : "Country"
          }
          isMulti={airportseasonSelectorConfig.dropdownMultiSelect}
          clearable
          closeOnSelect
          options={countryOpts}
          value={
            airportseasonSelectorConfig.dropdownMultiSelect
              ? countries
              : countries[0]
          }
          onChange={handleSelectCountries}
          menuPortalTarget={document.body}
        />
      </div>
      <div
        className={
          (airportseasonSelectorConfig.dropdownMultiSelect
            ? "ocs-w-220"
            : "ocs-w-100") +
          (airportseasonSelectorConfig.selectCodeType ? " ocs-mr-8" : "")
        }
      >
        <DropdownField
          name={FieldName.airports}
          required
          caption={
            airportseasonSelectorConfig.dropdownMultiSelect
              ? FlightText.airports
              : airport
          }
          isMulti={airportseasonSelectorConfig.dropdownMultiSelect}
          clearable={airportseasonSelectorConfig.dropdownMultiSelect}
          closeOnSelect
          options={airportOpts}
          hasError={hasError}
          errorMsg={errorMsg}
          touched={touched}
          value={
            airportseasonSelectorConfig.dropdownMultiSelect
              ? airports
              : predefinedAirport
          }
          onChange={handleSelectAirports}
          menuPortalTarget={document.body}
        />
      </div>
      {airportseasonSelectorConfig.selectCodeType && (
        <div>
          <RadioBooleanField
            value={useICAOCode}
            options={[
              { label: "IATA", value: false },
              { label: "ICAO", value: true }
            ]}
            onChange={handleChangeCodeType}
          />
        </div>
      )}
    </div>
  )
}

export default AirportSelector
